import { Icon, Popover, Spacer, Stack, Text } from '@chakra-ui/react';
import { AircraftType, SelectOption, ServiceType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { useState } from 'react';
import { HiChevronRight } from 'react-icons/hi';
import { HiBarsArrowDown } from 'react-icons/hi2';
import { useMediaQuery } from 'usehooks-ts';

import { AircraftFilter } from './AircraftFilter';
import { AirlineFilter } from './AirlineFilter';
import { AirportFilter } from './AirportFilter';
import { FilterPopoverButton, FilterPopoverContent } from './Popover';
import { ServiceTypeFilter } from './ServiceTypeFilter';
import { SortFilter } from './SortFilter';
import { StopFilter } from './StopFilter';

export type QuoteFilters = {
  providers: string[];
  origins: string[];
  destinations: string[];
  stops: string;
  aircraft: 'any' | AircraftType;
  serviceType: ServiceType;
  groupByProvider: boolean;
  sort: 'Cheapest' | 'Fastest';
};

type Props = {
  filters: QuoteFilters;
  origins: string[];
  destinations: string[];
  providers: SelectOption[];
  handleFilterChange: (key: string) => (nextValue: boolean | string | string[]) => void;
};

export const QuoteListFilters = ({
  filters,
  origins = [],
  destinations = [],
  providers = [],
  handleFilterChange,
}: Props) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [view, setView] = useState<string>('');
  const resetView = () => setView('');

  if (isMobile) {
    return (
      <Stack direction="row" align="center" justify="space-between">
        <Popover placement="bottom-start">
          <FilterPopoverButton label="Filters" />
          <FilterPopoverContent>
            <Show if={!view}>
              <Stack spacing="2" pt="2">
                {['Airlines', 'Airports', 'Stops', 'Aircraft Type', 'Service Type'].map((filter) => (
                  <Stack
                    key={filter}
                    direction="row"
                    align="center"
                    justify="space-between"
                    cursor="pointer"
                    onClick={() => setView(filter)}
                  >
                    <Text textStyle="captionRegular">{filter}</Text>
                    <Icon as={HiChevronRight} />
                  </Stack>
                ))}
              </Stack>
            </Show>
            <Show if={view === 'Airlines'}>
              <AirlineFilter
                filters={filters}
                providers={providers}
                handleFilterChange={handleFilterChange}
                resetView={resetView}
              />
            </Show>
            <Show if={view === 'Airports'}>
              <AirportFilter
                filters={filters}
                origins={origins}
                destinations={destinations}
                handleFilterChange={handleFilterChange}
                resetView={resetView}
              />
            </Show>
            <Show if={view === 'Stops'}>
              <StopFilter filters={filters} handleFilterChange={handleFilterChange} resetView={resetView} />
            </Show>
            <Show if={view === 'Aircraft Type'}>
              <AircraftFilter filters={filters} handleFilterChange={handleFilterChange} resetView={resetView} />
            </Show>
            <Show if={view === 'Service Type'}>
              <ServiceTypeFilter filters={filters} handleFilterChange={handleFilterChange} resetView={resetView} />
            </Show>
          </FilterPopoverContent>
        </Popover>
        <Popover placement="bottom-start">
          <FilterPopoverButton label={`Sort: ${filters.sort}`} icon={HiBarsArrowDown} />
          <FilterPopoverContent>
            <SortFilter filters={filters} handleFilterChange={handleFilterChange} resetView={resetView} />
          </FilterPopoverContent>
        </Popover>
      </Stack>
    );
  }

  return (
    <Stack direction="row" align="center" spacing="3">
      <Popover placement="bottom-start">
        <FilterPopoverButton
          label="Airlines"
          isSelected={filters.providers.length !== providers.length || !filters.groupByProvider}
        />
        <FilterPopoverContent>
          <AirlineFilter
            filters={filters}
            providers={providers}
            handleFilterChange={handleFilterChange}
            resetView={resetView}
          />
        </FilterPopoverContent>
      </Popover>

      <Popover placement="bottom-start">
        <FilterPopoverButton
          label="Airports"
          isSelected={filters.origins.length !== origins.length || filters.destinations.length !== destinations.length}
        />
        <FilterPopoverContent>
          <AirportFilter
            filters={filters}
            origins={origins}
            destinations={destinations}
            handleFilterChange={handleFilterChange}
            resetView={resetView}
          />
        </FilterPopoverContent>
      </Popover>

      <Popover placement="bottom-start">
        <FilterPopoverButton label="Stops" isSelected={filters.stops !== 'any'} />
        <FilterPopoverContent>
          <StopFilter filters={filters} handleFilterChange={handleFilterChange} resetView={resetView} />
        </FilterPopoverContent>
      </Popover>

      <Popover placement="bottom-start">
        <FilterPopoverButton label="Aircrafts" isSelected={filters.aircraft !== 'any'} />
        <FilterPopoverContent>
          <AircraftFilter filters={filters} handleFilterChange={handleFilterChange} resetView={resetView} />
        </FilterPopoverContent>
      </Popover>

      <Popover placement="bottom-start">
        <FilterPopoverButton label="Service Type" isSelected={filters.serviceType !== null} />
        <FilterPopoverContent>
          <ServiceTypeFilter filters={filters} handleFilterChange={handleFilterChange} resetView={resetView} />
        </FilterPopoverContent>
      </Popover>

      <Spacer />

      <Popover placement="bottom-start">
        <FilterPopoverButton label={`Sort: ${filters.sort}`} icon={HiBarsArrowDown} />
        <FilterPopoverContent>
          <SortFilter filters={filters} handleFilterChange={handleFilterChange} resetView={resetView} />
        </FilterPopoverContent>
      </Popover>
    </Stack>
  );
};
